/**
 * Web-side helpers for the `measurement` element type — the labels, option
 * lists and per-element memory the entry control and the card-definition
 * sub-editor share. Everything that converts, parses or formats lives in the
 * vendored shared module (`./units`); nothing here duplicates it.
 */
import { MAX_PRECISION, MEASURE_LIST, RATE_TIMES, allowedMeasures, canConvert, chooseDisplayUnit, formatInUnit, formatMeasurement, getMeasure, getUnit, isComposite, isMeasurementValue, isMultiMeasure, isOwnUnitId, isUnitOf, measureOfUnit, ownUnitAsUnit, ownUnitOf, parseMeasurement, readMeasurement, roundHalfAway, scopeOptions, splitComposite, unitIn, unitsOf } from './units';

/**
 * The human name for a unit FAMILY — what kind of quantity spelling it is
 * within its measure (registry.js). The registry carries the id, not the
 * word, because nothing server-side ever prints one; a family it does not
 * know falls back to its own id capitalised, so a family added on the API
 * side still reads as something rather than heading a blank section.
 */
const FAMILY_LABELS = {
	fluid: 'Liquid',
	cubic: 'Cubic',
	dry: 'Dry (US)',
	'fluid-uk': 'Imperial (UK)',
	general: 'General',
	electrical: 'Electrical & heating',
	weather: 'Weather',
	decimal: 'Decimal',
	binary: 'Binary',
	bits: 'Bits',
	// Data types PR 8. These measures split for `auto` and the unit-system
	// preference, and are short enough to stay flat lists (isFamilyGrouped).
	degrees: 'Degrees',
	radians: 'Radians',
	gradians: 'Gradians',
	turns: 'Turns',
	hertz: 'Hertz',
	rpm: 'Revolutions per minute',
	'mpg-us': 'Miles per US gallon',
	'mpg-uk': 'Miles per imperial gallon',
	'km-per-litre': 'Kilometers per liter',
	'litres-per-100km': 'Liters per 100 km',
	// Torque (KPR-1210): three units, one family each, for `auto` and the unit-system preference alone.
	'newton-metres': 'Newton-meters',
	'foot-pounds': 'Foot-pounds',
	'inch-pounds': 'Inch-pounds',
};

const plainFamilyLabel = (family) => FAMILY_LABELS[family] || (family ? family.charAt(0).toUpperCase() + family.slice(1) : '');

/** "per hour" for a rate time id ('h'); '' for anything else. */
export const perWord = (per) => {
	const time = RATE_TIMES.find(t => t.id === per);
	return time ? `per ${time.word}` : '';
};

/**
 * A RATE unit's family is its numerator's family per its time ('fluid/h',
 * registry.js "rates"): "Liquid per hour", or just "Per hour" for a measure
 * whose numerators are one family (mass, count).
 */
export const familyLabel = (family) => {
	const slash = typeof family === 'string' ? family.lastIndexOf('/') : -1;
	const per = slash === -1 ? '' : perWord(family.slice(slash + 1));
	if (!per) return plainFamilyLabel(family);
	const numerator = family.slice(0, slash);
	return numerator === 'default' ? per.charAt(0).toUpperCase() + per.slice(1) : `${plainFamilyLabel(numerator)} ${per}`;
};

/** The family ids of a measure, in registry order. [] for an unknown measure. */
export const familiesOf = (measureId) => {
	const seen = [];
	unitsOf(measureId).forEach(unit => { if (!seen.includes(unit.family)) seen.push(unit.family); });
	return seen;
};

// A short list reads better flat, whatever its families: angle (six units),
// frequency (five) and fuel economy (four, one family each) split so `auto`
// and the unit-system preference never cross a family, not to be headed.
const FLAT_UP_TO = 6;

/**
 * True when a measure splits into more than one family and is long enough to
 * need its pickers and its conversion modal grouped: volume, energy, pressure,
 * data size and the rates. Every other measure stays a flat list.
 */
export const isFamilyGrouped = (measureId) => familiesOf(measureId).length > 1 && unitsOf(measureId).length > FLAT_UP_TO;

/** "Pounds (lb)"; composites read "Pounds & ounces (lb-oz)". */
export const unitOptionLabel = (unit) => {
	if (!unit) return '';
	// An element's own unit (KPR-1109) reads by its names: "Smidgens (smidgen)".
	if (unit.own) return `${String(unit.plural).charAt(0).toUpperCase()}${String(unit.plural).slice(1)} (${unit.symbol})`;
	const label = unit.parts ? String(unit.label).replace(/\band\b/, '&') : unit.label;
	return `${label} (${unit.symbol})`;
};

/** The heading an element's own units sit under in a unit picker (KPR-1109). */
export const OWN_UNITS_LABEL = 'Own units';

/**
 * One antd Select option for a unit object. `searchText` is every spelling the
 * registry knows for the unit — its label, symbol, id and aliases — so a unit
 * picker finds pounds from "lb", "lbs" or "pound", the way the shared parser
 * reads "150 lbs" (optionSearchFilter folds case and "m³" → "m3").
 */
export const unitOption = (unit) => ({
	value: unit.id,
	label: unitOptionLabel(unit),
	searchText: [unit.label, unit.symbol, unit.id, ...(Array.isArray(unit.aliases) ? unit.aliases : [])]
		.filter(Boolean)
		.join(' '),
});

/**
 * A unit list as antd Select options: flat for a single-family measure (mass,
 * length, temperature and the rest — exactly as before), and grouped under the
 * family headings when the measure splits. Volume offers 31 units now; a flat
 * list of 31 is a wall, and one that quietly sits a US gallon next to an
 * imperial gallon 20% bigger is worse than a wall.
 */
export const groupUnitOptions = (units) => {
	const list = (units || []).filter(Boolean);
	// Units of SEVERAL measures (an element that allows volume, mass and count,
	// KPR-1085): one heading per measure ("Volume", "Mass", "Count"), and per
	// family inside a measure that splits ("Volume: Liquid", "Volume: Cubic"),
	// in the order the list gives them -- the element's measures, primary first.
	// An element's own units (KPR-1109), each a measure of one unit, share one
	// heading, after the rest.
	if (list.some(unit => unit.measure !== list[0].measure)) {
		const groups = [];
		list.forEach(unit => {
			if (unit.own) {
				let own = groups.find(g => g.key === 'own');
				if (!own) {
					own = { key: 'own', label: OWN_UNITS_LABEL, options: [] };
					groups.push(own);
				}
				own.options.push(unitOption(unit));
				return;
			}
			const measure = getMeasure(unit.measure);
			const split = isFamilyGrouped(unit.measure);
			const key = split ? `${unit.measure}:${unit.family}` : unit.measure;
			let group = groups.find(g => g.key === key);
			if (!group) {
				group = { key, label: split ? `${measure.label}: ${familyLabel(unit.family)}` : measure.label, options: [] };
				groups.push(group);
			}
			group.options.push(unitOption(unit));
		});
		const own = groups.findIndex(g => g.key === 'own');
		if (own !== -1) groups.push(groups.splice(own, 1)[0]);
		return groups;
	}
	if (!list.length || !isFamilyGrouped(list[0].measure)) return list.map(unitOption);
	const groups = [];
	list.forEach(unit => {
		let group = groups.find(g => g.key === unit.family);
		if (!group) {
			group = { key: unit.family, label: familyLabel(unit.family), options: [] };
			groups.push(group);
		}
		group.options.push(unitOption(unit));
	});
	return groups;
};

/**
 * Select options for the units of a measure, in registry order. For a RATE
 * measure, `per` narrows them to one time ('h': mL/h, L/h, gal/h, ...) — the
 * list a picker beside a "Per" choice offers; without it, every time.
 */
export const unitOptionsFor = (measureId, per = null) => groupUnitOptions(
	per ? unitsOf(measureId).filter(unit => unit.per === per) : unitsOf(measureId)
);

// ---------------------------------------------------------------- rates
//
// A rate is a measure of its own (registry.js "rates": `volume-rate` is
// volume per time, its units `mL/h`, `L/min`, …, each carrying the
// `numerator` and `per` it is made of). The editors never offer one in the
// measure list: an author picks the measure it counts and a Per beside it
// (docs/plans/measurement-rates.md), which is what the helpers below map.

/** The "Not a rate" choice of a Per picker. */
export const PER_NONE = 'none';

/** The measure picker's list: every measure that is not a rate. */
export const measurePickerOptions = () => MEASURE_LIST
	.filter(measure => !measure.rateOf)
	.map(measure => ({ value: measure.id, label: measure.label, searchText: measure.label }));

/**
 * The rate measure that counts `measureId` per time, or null (length,
 * temperature, …, and no measure at all — every plain measure's `rateOf` is
 * null, so a null id must never be looked up).
 */
export const rateMeasureOf = (measureId) => (typeof measureId === 'string' && measureId
	? MEASURE_LIST.find(measure => measure.rateOf === measureId) || null
	: null);

/** A Per picker's options for a measure: "Not a rate" and every time; [] for a measure with no rate. */
export const perOptionsFor = (measureId) => (rateMeasureOf(measureId)
	? [{ value: PER_NONE, label: 'Not a rate' }, ...RATE_TIMES.map(time => ({ value: time.id, label: perWord(time.id) }))]
	: []);

/**
 * What the Measure and Per pickers show for an element's options:
 * `{ measure, per }` — `volume` and `'h'` for a volume-rate element in mL/h,
 * `mass` and PER_NONE for a plain mass element, `measure: null` when none is
 * set.
 */
export const measurePickerValue = (options = {}) => {
	const measure = getMeasure(options?.measure);
	if (!measure) return { measure: null, per: PER_NONE };
	if (!measure.rateOf) return { measure: measure.id, per: PER_NONE };
	return { measure: measure.rateOf, per: getUnit(defaultUnitFor(options))?.per || getUnit(measure.canonical).per };
};

/**
 * True when the element would STORE a value in `unitId`, by the API's own rule
 * (units/normalize.js): a unit of the measure, inside the allowlist when there
 * is one. Wider than allowedUnitsFor, which is what a picker OFFERS — a rate's
 * picker shows one time, but the element accepts every time.
 */
export const acceptsUnit = (options = {}, unitId) => {
	// An element's own unit (KPR-1109) is always allowed: the allowlist lists
	// built-in units.
	if (isOwnUnitId(unitId)) return !!ownUnitOf(unitId, options);
	// Several measures (KPR-1085): a unit of any of them, inside the one allowlist.
	if (isMultiMeasure(options)) {
		if (!allowedMeasures(options).includes(measureOfUnit(unitId))) return false;
		const allowlist = Array.isArray(options.units) ? options.units : [];
		return !allowlist.length || allowlist.includes(unitId) || unitId === defaultUnitFor(options);
	}
	const measure = getMeasure(options?.measure);
	if (!measure || !isUnitOf(unitId, measure.id)) return false;
	const allowlist = Array.isArray(options.units) ? options.units.filter(id => isUnitOf(id, measure.id)) : [];
	return !allowlist.length || allowlist.includes(unitId) || unitId === defaultUnitFor(options);
};

/** The time a rate unit is per ('h'), or null for anything else. */
export const perOf = (unitId) => getUnit(unitId)?.per || null;

/**
 * The same numerator per another time: withPer('mL/h', 'min') is 'mL/min'.
 * Anything that is not a rate unit — 'auto', 'entered', a plain unit — comes
 * back unchanged.
 */
export const withPer = (unitId, per) => {
	const unit = getUnit(unitId);
	if (!unit?.numerator) return unitId;
	const id = `${unit.numerator}/${per}`;
	return isUnitOf(id, unit.measure) ? id : unitId;
};

// Every option that names a unit AT THE ELEMENT'S OLD TIME, moved to the new
// time with its number kept. A unit the author set at another time on purpose
// (a per-day display on a per-hour pump) stays where it is, and the allowlist
// never collapses: an entry whose move would land on one already allowed
// stays too, so mL/h + mL/min moved to per minute still allows both.
const movePer = (options, per) => {
	const from = perOf(options.defaultUnit);
	const move = id => (from && perOf(id) === from ? withPer(id, per) : id);
	const next = { ...options, defaultUnit: move(options.defaultUnit) };
	if (Array.isArray(options.units)) {
		const units = [];
		options.units.forEach(id => {
			const moved = move(id);
			const kept = moved !== id && (options.units.includes(moved) || units.includes(moved)) ? id : moved;
			if (!units.includes(kept)) units.push(kept);
		});
		// The API refuses an allowlist without the default unit.
		if (units.length && !units.includes(next.defaultUnit)) units.push(next.defaultUnit);
		next.units = units;
	}
	if (options.display && typeof options.display === 'object' && typeof options.display.unit === 'string') {
		next.display = { ...options.display, unit: move(options.display.unit) };
	}
	if (Array.isArray(options.displayRules)) {
		next.displayRules = options.displayRules.map(rule => (rule && typeof rule.unit === 'string' ? { ...rule, unit: move(rule.unit) } : rule));
	}
	if (isMeasurementValue(options.defaultValue)) next.defaultValue = { ...options.defaultValue, unit: move(options.defaultValue.unit) };
	return next;
};

/**
 * The element options after the Measure or the Per picker changes — the one
 * transition the card editor (MeasurementOptions) and the multi-add input
 * editor share. `scopedKeys` are the options that name a unit or a bound in
 * one; `measureId` is a picker value (never a rate), `per` a time id or
 * PER_NONE.
 *
 *   - Another time for the same rate is the same measure: every unit-bearing
 *     option moves to the new time and keeps its number as typed, the way a
 *     new default unit keeps min and max as typed.
 *   - Anything else — another measure, or a rate turned on or off — is a new
 *     measure: every scoped key goes, as a measure change always did, and the
 *     default unit restarts, keeping the unit it counts where the new measure
 *     has it (Volume in mL, then per hour: mL/h; then Not a rate: mL again).
 */
export const applyMeasurePicker = (options = {}, measureId, per, scopedKeys = []) => {
	const current = measurePickerValue(options);
	const base = getMeasure(measureId);
	const rate = base ? rateMeasureOf(base.id) : null;
	const wantsRate = !!rate && !!per && per !== PER_NONE && RATE_TIMES.some(time => time.id === per);
	if (base && wantsRate && current.measure === base.id && current.per !== PER_NONE) {
		return current.per === per ? options : movePer(options, per);
	}
	const kept = Object.fromEntries(Object.entries(options || {}).filter(([key]) => !scopedKeys.includes(key) && key !== 'measure' && key !== 'defaultUnit'));
	if (!base) return kept;
	const next = wantsRate ? rate : base;
	const from = getUnit(options?.defaultUnit);
	const counted = from ? (from.numerator || (from.parts ? from.parts[0] : from.id)) : null;
	const numerator = counted && isUnitOf(counted, base.id) ? counted : base.canonical;
	const candidate = wantsRate ? `${numerator}/${per}` : numerator;
	return { ...kept, measure: next.id, defaultUnit: isUnitOf(candidate, next.id) ? candidate : next.canonical };
};

/**
 * The element's default unit id: `options.defaultUnit` when it belongs to the
 * measure, else the measure's canonical unit (the same fallback the shared
 * normaliser and formatter use).
 */
export const defaultUnitFor = (options = {}) => {
	const measure = getMeasure(options?.measure);
	if (!measure) return null;
	return isUnitOf(options.defaultUnit, measure.id) ? options.defaultUnit : measure.canonical;
};

/**
 * The units a user may ENTER for the element: the `units` allowlist when it
 * names any unit of the measure, else every unit of the measure. The default
 * unit is always offered (the API rejects an allowlist without it).
 */
export const allowedUnitsFor = (options = {}) => {
	const measure = getMeasure(options?.measure);
	if (!measure) return [];
	// Several measures (KPR-1085): each measure's units in the element's order,
	// the primary first, each read as a one-measure element of that measure
	// would read it (registry.js scopeOptions) -- the allowlist's units of it,
	// or all of them (a rate: one time).
	if (isMultiMeasure(options)) {
		const out = [];
		allowedMeasures(options).forEach(id => {
			const scoped = scopeOptions(options, id, null) || {};
			// An own unit (KPR-1109): itself, in the unit shape.
			const units = isOwnUnitId(id) ? [ownUnitAsUnit(ownUnitOf(id, options))]
				: id === measure.id ? allowedUnitsFor(scoped) : allowedOtherUnits(id, scoped);
			units.forEach(unit => { if (unit && !out.some(u => u.id === unit.id)) out.push(unit); });
		});
		return out;
	}
	const fallback = defaultUnitFor(options);
	// A rate with no allowlist offers the default unit's own time — mL/h,
	// L/h, gal/h, … — not all five times of every unit: a picker of 155
	// volume rates is a wall, and the time a rate was set up in is part of
	// what it says. The API still reads any unit of the measure (an import's
	// "2 L/min"), and a stored value keeps its own unit in the control.
	const per = measure.rateOf ? perOf(fallback) : null;
	const all = per ? unitsOf(measure.id).filter(unit => unit.per === per) : unitsOf(measure.id);
	const allowlist = Array.isArray(options.units) ? options.units.filter(id => isUnitOf(id, measure.id)) : [];
	// In the author's order when there is an allowlist (they put the common
	// unit first), else the registry's (metric then imperial, small to large).
	const chosen = allowlist.length ? allowlist.map(id => getUnit(id)) : all;
	if (fallback && !chosen.some(unit => unit.id === fallback)) chosen.unshift(getUnit(fallback));
	return chosen;
};

/**
 * The units another measure of a multi-measure element offers: the allowlist's
 * units of it in the author's order, else every unit of it (a rate: the
 * canonical unit's own time). No default unit is added -- the element's one
 * default unit is in the primary measure.
 */
const allowedOtherUnits = (measureId, scoped) => {
	const measure = getMeasure(measureId);
	if (!measure) return [];
	const allowlist = Array.isArray(scoped.units) ? scoped.units.filter(id => isUnitOf(id, measure.id)) : [];
	if (allowlist.length) return allowlist.map(id => getUnit(id));
	const per = measure.rateOf ? perOf(measure.canonical) : null;
	return per ? unitsOf(measure.id).filter(unit => unit.per === per) : unitsOf(measure.id);
};

/** The measures an element allows, the primary first (KPR-1085; [measure] for one). */
export const measuresFor = (options = {}) => allowedMeasures(options || {});

/** "Volume, mass or count" -- the measures an element allows, in words (its own units, KPR-1109, by name: "Count, smidgen or dollop"). */
export const measuresLabel = (options = {}) => {
	const labels = allowedMeasures(options || {}).map(id => (isOwnUnitId(id) ? ownUnitOf(id, options)?.singular : getMeasure(id)?.label) || id);
	if (labels.length < 2) return labels[0] || '';
	const lower = labels.map((label, i) => (i === 0 ? label : label.toLowerCase()));
	return `${lower.slice(0, -1).join(', ')} or ${lower[lower.length - 1]}`;
};

/**
 * The symbol the number sits next to ("lb", "°C"); '' for an unknown unit.
 * `options`, the element's, name its own units (KPR-1109): "smidgen".
 */
export const unitSymbol = (unitId, options = null) => unitIn(unitId, options)?.symbol || '';

/** A composite's parts as unit objects plus its minor-per-major ratio, or null. */
export const compositeParts = (unitId) => {
	if (!isComposite(unitId)) return null;
	const unit = getUnit(unitId);
	return { major: getUnit(unit.parts[0]), minor: getUnit(unit.parts[1]), minorPerMajor: unit.minorPerMajor };
};

/**
 * What a stored value opens as in the entry control: `{ value, unit }` for
 * the stored object (in its stored unit, never converted), a legacy bare
 * number in the default unit, a string through the shared parser, and null
 * for anything empty or unreadable.
 */
export const readEntryValue = (raw, options = {}) => {
	if (isMeasurementValue(raw)) return { value: raw.value, unit: raw.unit };
	if (raw === undefined || raw === null || raw === '') return null;
	if (typeof raw === 'number' && Number.isFinite(raw)) {
		const unit = defaultUnitFor(options);
		return unit ? { value: raw, unit } : null;
	}
	if (typeof raw === 'string') {
		const parsed = parseMeasurement(raw, options);
		return parsed ? { value: parsed.value, unit: parsed.unit } : null;
	}
	return null;
};

/**
 * The unit a person last ENTERED a value in for this element, remembered in
 * the browser so a "kg person" filling a "lb card" picks their unit once.
 * Read only when the control opens on an empty value; a stored value always
 * opens in its own unit. Storage is optional (private mode, quota, tests):
 * every failure is a silent fallback to the element default.
 */
export const lastUsedUnitKey = (cardId, elementName) => `keepr.units.${cardId || 'card'}.${elementName || 'element'}`;

export const readLastUsedUnit = (cardId, elementName, options = {}) => {
	try {
		const stored = window.localStorage.getItem(lastUsedUnitKey(cardId, elementName));
		if (stored && allowedUnitsFor(options).some(unit => unit.id === stored)) return stored;
	} catch (e) {
		// storage unavailable — fall through
	}
	return null;
};

export const writeLastUsedUnit = (cardId, elementName, unitId) => {
	if (!unitId) return;
	try {
		window.localStorage.setItem(lastUsedUnitKey(cardId, elementName), unitId);
	} catch (e) {
		// storage unavailable — nothing to remember
	}
};

/** The fewest decimals (<= MAX_PRECISION) that print `n` exactly. */
const decimalsOf = (n) => {
	for (let d = 0; d < MAX_PRECISION; d++) {
		if (Math.abs(Math.round(n * 10 ** d) / 10 ** d - n) < 1e-9) return d;
	}
	return MAX_PRECISION;
};

/**
 * A `{ value, unit }` entry AS ENTERED: the unit it was typed in, at the
 * decimals it was typed with ("20 lb", "8 lb 7 oz", "37.2 °C") — no element
 * display rules applied. This is what a filter chip, a condition row and a
 * KQL literal carry: the shared parser reads every form this prints (the
 * kqlBuilder suite proves it for every unit in the registry), and the API's
 * template renderer prints the same text for an interpolated token. '' for
 * anything that is not a readable entry.
 */
export const formatEnteredMeasurement = (entry, options = null) => {
	if (!isMeasurementValue(entry)) return '';
	// An element's own unit (KPR-1109): by its name when the element's options
	// are at hand ("2 smidgens"), else by its id ("2 own:smidgen") -- a
	// spelling the parser reads for that unit on its element either way.
	if (isOwnUnitId(entry.unit)) {
		const own = ownUnitOf(entry.unit, options);
		if (!own) return `${entry.value} ${entry.unit}`;
		return formatMeasurement(
			{ value: entry.value, unit: own.id, base: entry.value },
			{ measure: own.id, defaultUnit: own.id, ownUnits: [own], display: { unit: 'entered', precision: decimalsOf(entry.value) } }
		);
	}
	const unit = getUnit(entry.unit);
	if (!unit) return '';
	const magnitude = unit.parts ? splitComposite(entry.value, unit.id).minor : entry.value;
	return formatMeasurement(
		{ value: entry.value, unit: unit.id },
		{ measure: unit.measure, defaultUnit: unit.id, display: { unit: 'entered', precision: decimalsOf(magnitude) } }
	);
};

/**
 * The formatter prefs for a viewer's unit-system preference: only a system
 * other than the card default changes anything (the shared formatter treats
 * an absent prefs object as "the card's rules").
 */
export const displayPrefsFor = (unitSystem) => (
	unitSystem === 'metric' || unitSystem === 'imperial' ? { unitSystem } : undefined
);

/**
 * The fewest decimals (<= cap) that print `n` ROUNDED AT `cap` exactly:
 * 14.6959 at cap 2 is "14.7", not "14.70".
 */
const trimmedDecimals = (n, cap) => {
	const rounded = roundHalfAway(n, cap);
	for (let d = 0; d < cap; d++) {
		if (Math.abs(roundHalfAway(rounded, d) - rounded) < 1e-9) return d;
	}
	return cap;
};

/**
 * Decimals for a CONVERTED magnitude in `unitId` — a glance at "what is this
 * in kg?", not the element's display precision (0 decimals for whole pounds
 * would print an infant's 3.83 kg as "4 kg"). Four significant figures
 * (68.22 kg, 2,406 oz, 0.06822 t), trailing zeros trimmed, never past
 * MAX_PRECISION; a composite's minor part gets at most two decimals.
 */
export const conversionPrecision = (magnitude, unitId) => {
	const unit = getUnit(unitId);
	if (!unit || !Number.isFinite(magnitude)) return 0;
	if (unit.parts) return trimmedDecimals(Math.abs(splitComposite(magnitude, unit.id).minor), 2);
	const abs = Math.abs(magnitude);
	if (abs === 0) return 0;
	const cap = Math.min(MAX_PRECISION, Math.max(0, 3 - Math.floor(Math.log10(abs))));
	return trimmedDecimals(abs, cap);
};

/**
 * A stored value in every CONVERTIBLE unit of its measure, for the
 * conversions modal (a RATE in the units of the time it is shown in, and in
 * its own unit at every other time: 100 mL/h beside 2.4 L/d, never all 155
 * volume rates): `[{ unit, family, text, entered, shown }]` in registry
 * order (by family, then metric before imperial, small to large). The row for
 * the unit the value was entered in prints it exactly as entered ("8 lb
 * 7 oz"); every other row converts from the base through conversionPrecision.
 * `shown` marks the unit the element's display rules (or the viewer's
 * `prefs`) pick, so the modal can say which row is the one on screen, and
 * `family` is what the modal groups by. [] for anything unreadable.
 *
 * A NON-CONVERTIBLE unit is offered only when it is the one the value was
 * entered in: "3 tablets" has no reading in puffs, and printing "3 puff /
 * 3 capsule / 3 pallet" underneath it -- which is what this did before the
 * filter -- is a category error dressed up as a conversion.
 */
export const conversionRows = (raw, options = {}, prefs = undefined) => {
	// An own unit (KPR-1109; the options scopeOptions gives one) converts to
	// nothing: its one row is the value as entered.
	if (isOwnUnitId(options?.measure)) {
		const own = ownUnitOf(options.measure, options);
		const m = readMeasurement(raw, options);
		const text = own && m && typeof m.base === 'number' ? formatMeasurement(m, options, prefs) : '';
		return text ? [{ unit: ownUnitAsUnit(own), family: 'own', text, entered: true, shown: true }] : [];
	}
	// A value of an element that allows several measures converts within its
	// OWN measure (KPR-1085): 250 g in kilograms and ounces, never in cups.
	if (isMultiMeasure(options)) {
		const read = readMeasurement(raw, options);
		const scoped = read ? scopeOptions(options, measureOfUnit(read.unit), read.unit) : null;
		return scoped ? conversionRows(read, scoped, prefs) : [];
	}
	const measure = getMeasure(options?.measure);
	const m = readMeasurement(raw, options);
	if (!measure || !m || typeof m.base !== 'number' || !Number.isFinite(m.base)) return [];
	const shown = chooseDisplayUnit(m, options, prefs)?.unit || null;
	const anchor = getUnit(shown) || getUnit(m.unit);
	const numerators = [getUnit(m.unit)?.numerator, anchor?.numerator].filter(Boolean);
	return unitsOf(measure.id)
		.filter(unit => canConvert(m.unit, unit.id))
		.filter(unit => !measure.rateOf || unit.id === m.unit || unit.per === anchor?.per || numerators.includes(unit.numerator))
		.map(unit => {
			const entered = unit.id === m.unit;
			const text = entered
				? formatEnteredMeasurement({ value: m.value, unit: m.unit })
				: formatInUnit(m.base, unit.id, conversionPrecision(unit.fromBase(m.base), unit.id));
			return { unit, family: unit.family, text, entered, shown: unit.id === shown };
		})
		.filter(row => row.text);
};

/**
 * The conversions a person reaches for first, per unit: inches → feet, feet
 * and inches, centimeters; km → miles, feet, meters; lb-oz → pounds, ounces,
 * kilograms. The conversions modal leads with these and folds every other
 * unit of the measure under "All units", so the obvious answer is never a
 * search through 17 masses or 31 volumes. Usually the next unit up and down
 * in the same system, then the everyday unit of the other system.
 *
 * Web-only presentation, so it lives here and not in the vendored registry.
 * Every convertible plain unit has an entry (a test reads the registry), so a
 * unit added on the API side announces itself here. A rate takes its
 * numerator's entry at its own time (commonUnitIds).
 */
const COMMON_CONVERSIONS = {
	// mass
	mcg: ['mg', 'g'],
	mg: ['g', 'mcg'],
	g: ['oz', 'kg', 'mg'],
	kg: ['lb', 'lb-oz', 'g'],
	t: ['kg', 'ton', 'lb'],
	oz: ['g', 'lb'],
	lb: ['kg', 'oz', 'lb-oz'],
	st: ['st-lb', 'lb', 'kg'],
	ton: ['lb', 't'],
	'lb-oz': ['lb', 'oz', 'kg'],
	'st-lb': ['lb', 'kg'],
	gr: ['mg', 'g'],
	dr: ['g', 'oz'],
	longton: ['t', 'ton', 'lb'],
	ozt: ['g', 'oz', 'dwt'],
	dwt: ['g', 'ozt'],
	ct: ['g', 'mg'],
	// length
	mm: ['cm', 'in'],
	cm: ['in', 'mm', 'm'],
	m: ['ft', 'ft-in', 'cm'],
	km: ['mi', 'ft', 'm'],
	in: ['ft', 'ft-in', 'cm'],
	ft: ['in', 'ft-in', 'm'],
	yd: ['ft', 'm'],
	mi: ['km', 'ft'],
	nmi: ['km', 'mi'],
	'ft-in': ['ft', 'in', 'cm'],
	micron: ['mm', 'mil'],
	mil: ['micron', 'mm', 'in'],
	ftm: ['m', 'ft'],
	// temperature
	C: ['F', 'K'],
	F: ['C', 'K'],
	K: ['C', 'F'],
	// volume
	mL: ['floz', 'cup', 'L'],
	cL: ['mL', 'floz'],
	dL: ['mL', 'cup'],
	L: ['gal', 'qt', 'mL'],
	hL: ['L', 'gal'],
	kL: ['L', 'm3', 'gal'],
	tsp: ['mL', 'tbsp'],
	tbsp: ['mL', 'tsp'],
	floz: ['mL', 'cup'],
	cup: ['mL', 'floz', 'tbsp'],
	pt: ['L', 'cup', 'floz'],
	qt: ['L', 'gal', 'pt'],
	gal: ['L', 'qt'],
	bbl: ['gal', 'L'],
	mm3: ['cm3', 'mL'],
	cm3: ['mL', 'in3'],
	dm3: ['L', 'cm3'],
	m3: ['L', 'ft3', 'yd3'],
	in3: ['cm3', 'mL'],
	ft3: ['m3', 'gal', 'L'],
	yd3: ['ft3', 'm3'],
	'acre-ft': ['m3', 'gal'],
	fbm: ['ft3', 'in3'],
	drypt: ['L', 'dryqt'],
	dryqt: ['L', 'drypt'],
	peck: ['L', 'bu'],
	bu: ['L', 'peck', 'ft3'],
	impfloz: ['mL', 'floz'],
	imppt: ['L', 'pt', 'mL'],
	impqt: ['L', 'qt'],
	impgal: ['L', 'gal'],
	// duration
	ms: ['s'],
	s: ['ms', 'min'],
	min: ['s', 'h'],
	h: ['min', 'h-min', 'd'],
	d: ['h', 'wk'],
	wk: ['d'],
	'h-min': ['min', 'h'],
	'min-s': ['s', 'min'],
	// area
	cm2: ['in2', 'm2'],
	m2: ['ft2', 'yd2'],
	ha: ['acre', 'm2'],
	km2: ['mi2', 'ha'],
	ft2: ['m2', 'yd2'],
	yd2: ['m2', 'ft2'],
	acre: ['ha', 'ft2', 'm2'],
	mi2: ['km2', 'acre'],
	mm2: ['cm2', 'in2'],
	in2: ['cm2', 'ft2'],
	// speed
	'm/s': ['km/h', 'mph'],
	'km/h': ['mph', 'm/s'],
	mph: ['km/h', 'ft/s'],
	kn: ['km/h', 'mph'],
	'ft/s': ['m/s', 'mph'],
	// energy
	J: ['cal', 'kJ'],
	kJ: ['kcal', 'J'],
	cal: ['J', 'kcal'],
	kcal: ['kJ', 'cal'],
	MJ: ['kWh', 'kJ'],
	Wh: ['kWh', 'kJ'],
	kWh: ['MJ', 'BTU'],
	BTU: ['kWh', 'kJ'],
	// pressure
	Pa: ['kPa', 'psi'],
	kPa: ['psi', 'bar'],
	bar: ['psi', 'kPa'],
	psi: ['kPa', 'bar'],
	mmHg: ['kPa', 'inHg'],
	inHg: ['mmHg', 'hPa'],
	MPa: ['psi', 'bar'],
	atm: ['kPa', 'psi', 'bar'],
	hPa: ['inHg', 'mmHg'],
	// data size
	B: ['KB', 'bit'],
	KB: ['B', 'MB', 'KiB'],
	MB: ['KB', 'GB', 'MiB'],
	GB: ['MB', 'TB', 'GiB'],
	TB: ['GB', 'TiB'],
	PB: ['TB'],
	bit: ['B'],
	KiB: ['KB', 'B'],
	MiB: ['MB', 'KiB'],
	GiB: ['GB', 'MiB'],
	TiB: ['TB', 'GiB'],
	// angle
	deg: ['rad', 'arcmin'],
	arcmin: ['deg', 'arcsec'],
	arcsec: ['arcmin', 'deg'],
	rad: ['deg', 'turn'],
	grad: ['deg'],
	turn: ['deg', 'rad'],
	// frequency
	Hz: ['kHz', 'rpm'],
	kHz: ['Hz', 'MHz'],
	MHz: ['kHz', 'GHz'],
	GHz: ['MHz'],
	rpm: ['Hz'],
	// fuel economy: four spellings, so every one is common
	mpg: ['L/100km', 'km/L', 'mpg-uk'],
	'mpg-uk': ['L/100km', 'mpg', 'km/L'],
	'km/L': ['L/100km', 'mpg', 'mpg-uk'],
	'L/100km': ['mpg', 'km/L', 'mpg-uk'],
	// torque: a lug nut is read in either, a small screw in inch-pounds
	'N-m': ['ft-lb', 'in-lb'],
	'ft-lb': ['N-m', 'in-lb'],
	'in-lb': ['N-m', 'ft-lb'],
};

/** A rate's next longer time, for its one cross-time common row: 100 mL/h is 2,400 mL/d. */
const LONGER_TIME = { s: 'min', min: 'h', h: 'd', d: 'wk' };

/** Whether COMMON_CONVERSIONS answers for a plain unit (the registry-coverage test reads it). */
export const hasCommonConversions = (unitId) => Object.prototype.hasOwnProperty.call(COMMON_CONVERSIONS, unitId);

/**
 * commonUnitIds(unitId) -> string[]
 *
 * The unit ids to lead the conversions modal with for a value read in
 * `unitId`, most obvious first. A rate takes its numerator's list at its own
 * time (mL/h → fl oz/h, cup/h, L/h) and adds itself at the next longer time
 * (mL/d), which is the only common row a count rate has (20 drops/min is
 * 1,200 drops/h). [] for a unit with no list.
 */
export const commonUnitIds = (unitId) => {
	const unit = getUnit(unitId);
	if (!unit) return [];
	if (!unit.per) return COMMON_CONVERSIONS[unit.id] || [];
	const sameTime = (COMMON_CONVERSIONS[unit.numerator] || []).map(id => `${id}/${unit.per}`);
	const longer = LONGER_TIME[unit.per];
	return longer ? [...sameTime, `${unit.numerator}/${longer}`] : sameTime;
};

/**
 * splitConversionRows(rows) -> { common, rest }
 *
 * conversionRows split for the modal: `common` is the row the value was
 * entered in, the row it is shown in, then commonUnitIds of the SHOWN unit
 * (the one on screen is what the reader is converting from), in that order
 * and once each; `rest` is every other row, in registry order. Ids that
 * conversionRows did not list (a rate at a time it filtered out, a unit
 * that does not convert) simply do not appear.
 */
export const splitConversionRows = (rows) => {
	const byId = new Map(rows.map(row => [row.unit.id, row]));
	const entered = rows.find(row => row.entered);
	const anchor = rows.find(row => row.shown) || entered;
	if (!anchor) return { common: [], rest: rows };
	const ids = [entered?.unit.id, anchor.unit.id, ...commonUnitIds(anchor.unit.id)];
	const common = [...new Set(ids)].map(id => byId.get(id)).filter(Boolean);
	const commonIds = new Set(common.map(row => row.unit.id));
	return { common, rest: rows.filter(row => !commonIds.has(row.unit.id)) };
};

/** The `when` bound of a display rule as [comparator, number], or null. */
export const ruleBound = (when) => {
	if (!when || typeof when !== 'object') return null;
	for (const key of ['lt', 'lte', 'gt', 'gte']) {
		if (typeof when[key] === 'number' && Number.isFinite(when[key])) return [key, when[key]];
	}
	return null;
};

export const MAX_DISPLAY_RULES = 5;
